import { useEffect } from "react";

import { trackAttribution } from "@/lib/attribution.functions";

const GA4_MEASUREMENT_ID = "G-T7406MEZ2W";

/** Läser GA4 client id direkt ur _ga-cookien (synkront, ingen väntan). */
function readClientIdFromCookie(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)_ga=GA\d\.\d\.(\d+\.\d+)/);
  return match?.[1] ?? null;
}

/** Frågar gtag om client_id om taggen redan finns – annars null direkt. */
function readClientIdFromGtag(timeoutMs = 300): Promise<string | null> {
  const w = window as unknown as { gtag?: (...args: unknown[]) => void };
  const gtag = w.gtag;
  if (typeof gtag !== "function") return Promise.resolve(null);
  return new Promise((resolve) => {
    let settled = false;
    const done = (v: string | null) => {
      if (settled) return;
      settled = true;
      resolve(v || null);
    };
    try {
      gtag("get", GA4_MEASUREMENT_ID, "client_id", (id: string) => done(id));
    } catch {
      done(null);
    }
    setTimeout(() => done(null), timeoutMs);
  });
}

const ADTRACTION_HOST = /(^|\.)(adt\d+\.(com|net|se)|adtraction\.com)$/i;
const AFFILIATE_HOSTS = [ADTRACTION_HOST, /^do\.enklare\.se$/i, /^go\.creditstar\.se$/i];

function isAffiliateHref(href: string): boolean {
  try {
    const host = new URL(href, window.location.href).hostname;
    return AFFILIATE_HOSTS.some((re) => re.test(host));
  } catch {
    return false;
  }
}

export function AttributionTracker() {
  useEffect(() => {
    let cancelled = false;
    let sentCid: string | null = null;

    // First-touch-parametrarna läses direkt vid sidladdning, så de behålls
    // även om användaren hinner navigera vidare innan cid finns.
    const params = new URLSearchParams(window.location.search);
    const p = (k: string) => params.get(k);
    const payloadBase = {
      landing_page: window.location.pathname + window.location.search,
      referrer: document.referrer || null,
      utm_source: p("utm_source"),
      utm_medium: p("utm_medium"),
      utm_campaign: p("utm_campaign"),
      utm_content: p("utm_content"),
      utm_term: p("utm_term"),
      fbclid: p("fbclid"),
      gclid: p("gclid"),
    };

    const send = (cid: string) => {
      if (!cid || cid === sentCid) return;
      sentCid = cid;
      void trackAttribution({ data: { cid, ...payloadBase } }).catch(() => {
        /* attribution får aldrig störa sidan */
      });
    };

    /** Försöker hitta cid nu; returnerar true om attribution skickades. */
    const attempt = async (): Promise<boolean> => {
      if (cancelled || sentCid) return true;
      const cid = readClientIdFromCookie() ?? (await readClientIdFromGtag());
      if (!cid || cancelled) return false;
      send(cid);
      return true;
    };

    // 1) Direkt försök vid mount – återkommande besökare har redan _ga-cookien.
    void attempt();

    // 2) Snabb polling (100 ms) i stället för en fast paus, så att raden skapas
    //    i samma ögonblick som GA4 sätter cookien.
    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      if (cancelled || sentCid) {
        window.clearInterval(interval);
        return;
      }
      if (Date.now() - startedAt > 30000) {
        window.clearInterval(interval);
        return;
      }
      void attempt();
    }, 100);

    // 3) Sista chansen: vid klick på en affiliate-CTA försöker vi läsa cid och
    //    skicka attribution innan webbläsaren lämnar sidan (capture-fas).
    const onPointer = (e: Event) => {
      if (sentCid) return;
      const target = e.target as Element | null;
      const link = target?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!link || !isAffiliateHref(link.getAttribute("href") || "")) return;
      const cid = readClientIdFromCookie();
      if (cid) send(cid);
    };
    document.addEventListener("pointerdown", onPointer, { capture: true, passive: true });
    document.addEventListener("click", onPointer, { capture: true });

    // 4) Om fliken varit dold när cookien sattes: försök igen när den blir synlig.
    const onVisible = () => {
      if (document.visibilityState === "visible") void attempt();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener("pointerdown", onPointer, { capture: true } as EventListenerOptions);
      document.removeEventListener("click", onPointer, { capture: true } as EventListenerOptions);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  return null;
}
